/**
 * Bilingual message catalogue (CZ / EN). Every key carries BOTH languages on the same
 * line, so a missing translation is structurally impossible — the type system and
 * `npm run typecheck` both enforce parity.
 *
 * Placeholders use `{name}` and are interpolated by `t()` in `src/i18n/index.ts`.
 */
export type Lang = 'cs' | 'en';

export const LANGS: { id: Lang; label: string; flag: string; title: string }[] = [
  { id: 'cs', label: 'CZ', flag: '🇨🇿', title: 'Přepnout do češtiny' },
  { id: 'en', label: 'EN', flag: '🇬🇧', title: 'Switch to English' },
];

export interface LocalizedText {
  cs: string;
  en: string;
}

export const MESSAGES: Record<string, LocalizedText> = {
  // ---- common ----
  // ---- urban expansion spotlight ----
  'expansion.spotlightTitle': { cs: 'Nová výstavba / New Property Zoned', en: 'New Property Zoned' },
  'expansion.spotlightSubtitle': { cs: 'Město zónovalo novou parcelu — IPO tranše se otevírají', en: 'The city zoned a new parcel — IPO tranches open' },
  'expansion.initialValuation': { cs: 'Počáteční hodnota', en: 'Initial valuation' },
  'expansion.initialRentYield': { cs: 'Vyvolávací nájem & výnos', en: 'Initial rent & yield' },
  'expansion.publicFloat': { cs: 'Dostupné akcie', en: 'Public float' },
  'expansion.purchaseRules': { cs: 'K dispozici k odkupu při vstupu na toto pole nebo v aukci.', en: 'Available to buy when you land on the tile or at auction.' },
  'expansion.viewOnBoard': { cs: 'Prohlédnout na desce', en: 'View on board' },
  'common.appTagline': { cs: 'Zlomkové akcie · governance · makro cykly', en: 'Fractional equity · governance · macro cycles' },
  'common.cash': { cs: 'Hotovost', en: 'Cash' },
  'common.equityValue': { cs: 'Hodnota pozic', en: 'Equity value' },
  'common.netWorth': { cs: 'Čisté jmění', en: 'Net worth' },
  'common.debt': { cs: 'Dluh', en: 'Debt' },
  'common.ceiling': { cs: 'Limit', en: 'Ceiling' },
  'common.loans': { cs: 'Úvěry', en: 'Loans' },
  'common.investments': { cs: 'Investice', en: 'Investments' },
  'common.saveLoad': { cs: '💾 Uložit / Načíst', en: '💾 Save / Load' },
  'common.round': { cs: 'Kolo', en: 'Round' },
  'common.phase': { cs: 'Fáze', en: 'Phase' },
  'common.close': { cs: 'Zavřít', en: 'Close' },
  'common.inspect': { cs: 'Detail', en: 'Inspect' },
  'common.upgrade': { cs: 'Vylepšit', en: 'Upgrade' },
  'common.vote': { cs: 'Hlasovat', en: 'Vote' },
  'common.frozen': { cs: '🧊 uzavřeno', en: '🧊 frozen' },
  'common.yield': { cs: 'Výnos', en: 'Yield' },
  'common.rent': { cs: 'Nájem', en: 'Rent' },
  'common.perRound': { cs: '/kolo', en: '/round' },
  'common.rounds': { cs: 'kol', en: 'rounds' },
  'common.you': { cs: 'Ty', en: 'You' },
  'common.activeCount': { cs: 'aktivní', en: 'active' },
  'common.publicFloat': { cs: 'Volný float', en: 'Public float' },
  'common.others': { cs: 'Ostatní', en: 'Others' },
  'common.shares': { cs: 'akcií', en: 'shares' },
  'common.monopolyBonus': { cs: '★ MONOPOL +25 %', en: '★ MONOPOLY +25 %' },
  'common.ownedBadge': { cs: '{owned}/{total} vlastněno', en: '{owned}/{total} owned' },
  'common.yourCut': { cs: 'tvůj díl {amount}/kolo', en: 'you {amount}/round' },

  // ---- header / shell ----
  'header.heatmap': { cs: '🔥 Heatmapa', en: '🔥 Heatmap' },
  'header.fast': { cs: 'Rychle', en: 'Fast' },
  'header.normal': { cs: 'Normálně', en: 'Normal' },
  'header.trade': { cs: '🤝 Obchod', en: '🤝 Trade' },
  'header.endless': { cs: 'Nekonečná / cíl $100k', en: 'Endless / target $100k' },
  'header.classic25': { cs: 'Klasika 25 kol', en: 'Classic 25 rounds' },
  'header.gameLength': {
    cs: 'Délka hry: nekonečná / cíl $100 000, nebo klasických 25 kol',
    en: 'Game length: endless / $100,000 target, or classic 25 rounds',
  },
  'header.newGame': { cs: '🔄 Nová hra', en: '🔄 New Game' },
  'header.newGameConfirm': { cs: 'Zpět do lobby? Rozdělaná hra se ztratí.', en: 'Return to the setup lobby? The current match will be lost.' },
  'header.board': { cs: '🗺️ Deska', en: '🗺️ Board' },
  'header.actionTab': { cs: '⚡ Akce', en: '⚡ Action' },
  'header.portfolioTab': { cs: '📊 Portfolio', en: '📊 Portfolio' },
  'header.panHint': { cs: '🖐 Celá deska vždy na obrazovce — klepni na pole pro detail', en: '🖐 The whole board always fits — tap a tile to inspect' },
  'board.botTurnNotice': { cs: 'Tah soupeře', en: 'Opponent Turn' },
  'board.botPlaying': { cs: 'Vyhodnocuje strategii…', en: 'Evaluating strategy…' },
  'nav.board': { cs: 'Deska', en: 'Board' },
  'nav.portfolio': { cs: 'Portfolio', en: 'Portfolio' },
  'nav.finance': { cs: 'Finance', en: 'Finance' },
  'nav.rivals': { cs: 'Hráči', en: 'Rivals' },

  // ---- auth / cloud ----
  'auth.signIn': { cs: '🔑 Přihlásit se přes Google', en: '🔑 Sign in with Google' },
  'auth.signOut': { cs: 'Odhlásit', en: 'Logout' },
  'auth.signedInAs': { cs: 'Přihlášen jako {name}', en: 'Signed in as {name}' },
  'auth.cloudOff': { cs: 'Cloudová úložiště nejsou nakonfigurována', en: 'Cloud saves are not configured' },
  'auth.localOnly': { cs: 'Hraješ lokálně — postup se ukládá jen v prohlížeči.', en: 'Playing locally — progress is stored in this browser only.' },
  'auth.signInFailed': { cs: 'Přihlášení selhalo: {error}', en: 'Sign-in failed: {error}' },
  'cloud.saving': { cs: 'Ukládám do cloudu…', en: 'Saving to cloud…' },
  'cloud.saved': { cs: 'Uloženo {time} · {round}. kolo', en: 'Saved {time} · round {round}' },
  'cloud.error': { cs: 'Cloudové uložení selhalo ({error}) — hraješ dál lokálně.', en: 'Cloud save failed ({error}) — continuing locally.' },
  'cloud.resumeTitle': { cs: '☁️ Našli jsme uloženou hru', en: '☁️ Cloud save found' },
  'cloud.resumeBody': {
    cs: '{name}, v cloudu máš rozehranou partii ({round}. kolo, {phase}, {players}). Chceš v ní pokračovat?',
    en: '{name}, your cloud save holds a match in progress (round {round}, {phase}, {players}). Resume it?',
  },
  'cloud.resumeCta': { cs: '▶️ Pokračovat v uložené hře', en: '▶️ Resume Cloud Game' },
  'cloud.newCta': { cs: '🆕 Nová hra', en: '🆕 New Game' },
  'cloud.historyNote': { cs: 'Účet {email} · historie: users/{uid}/games', en: 'Account {email} · history: users/{uid}/games' },
  'cloud.restored': { cs: '☁️ Načteno z cloudu: {round}. kolo, {phase}, {players}.', en: '☁️ Cloud save restored: round {round}, {phase}, {players}.' },
  'cloud.uploaded': { cs: '☁️ Cloudové uložení dokončeno ({round}. kolo).', en: '☁️ Cloud save complete (round {round}).' },
  'cloud.archived': { cs: '☁️ Předchozí partie uložena do historie.', en: '☁️ Previous match archived to cloud history.' },


  // ---- action card ----
  'action.rollDice': { cs: '🎲 Hoď kostkou', en: '🎲 Roll Dice' },
  'action.stopHere': { cs: '📍 Zastavit tady', en: '📍 Stop Here' },
  'action.pushLuck': { cs: '🔥 Riskovat víc (2× výplata / 2× nájem)', en: '🔥 Push Your Luck (2× payout / 2× rent)' },
  'action.payContinue': { cs: '💸 Zaplatit a pokračovat', en: '💸 Pay & Continue' },
  'action.endTurn': { cs: '➡️ Ukončit tah', en: '➡️ End Turn' },
  'action.buyDirect': { cs: '🛒 Koupit přímo', en: '🛒 Buy directly' },
  'action.auction': { cs: '🔨 Pustit do aukce', en: '🔨 Send to auction' },
  'action.pass': { cs: '🚶 Přejít', en: '🚶 Pass' },
  'action.voteFor': { cs: '✅ Pro', en: '✅ For' },
  'action.voteAgainst': { cs: '⛔ Proti', en: '⛔ Against' },
  'action.drawer': { cs: '💼 Šuplík: pojištění · obchody', en: '💼 Drawer: insurance · deals' },
  'action.yourTurn': { cs: '🧑 {name} — tvůj tah', en: '🧑 {name} — your turn' },
  'action.botPlaying': { cs: '🤖 {name} (bot) — hraje…', en: '🤖 {name} (bot) — playing…' },
  'action.opponentsAuto': {
    cs: 'Soupeři hrají sami — nahoře na desce vidíš, co právě dělají.',
    en: 'Rivals play themselves — follow their moves on the board.',
  },
  'action.botWait': { cs: 'Soupeři přemýšlí…', en: 'Rivals are thinking…' },
  'action.rentDue': {
    cs: '{district} · držíš {pct} % · nájem ze zbytku {rent} · výnos {yield}',
    en: '{district} · you hold {pct}% · rent on the rest {rent} · yield {yield}',
  },
  'action.insurance': { cs: '🛡️ Pojištění nájmu · {cost}', en: '🛡️ Rent insurance · {cost}' },
  'action.financialDesk': { cs: '🏦 Finanční deska · úvěry, spoření & pojištění', en: '🏦 Financial Desk · loans, savings & insurance' },
  'action.treasury': {
    cs: 'Úvěry {loans} · Investice {investments} · sazba {rate}/kolo',
    en: 'Loans {loans} · Investments {investments} · rate {rate}/round',
  },
  'action.tileGuide': { cs: 'ℹ️ Jak toto pole funguje', en: 'ℹ️ How this tile works' },

  // ---- ticker / bot events ----
  'ticker.botBought': { cs: '{name} koupil {shares} akcií {tile} za {cost}', en: '{name} bought {shares} shares of {tile} for {cost}' },
  'ticker.botBoughtWhy': {
    cs: '{name} koupil {shares} akcií {tile} za {cost} — {why}',
    en: '{name} bought {shares} shares of {tile} for {cost} — {why}',
  },
  'ticker.botPasses': { cs: '{name} přešel {tile} — {why}', en: '{name} passed on {tile} — {why}' },
  'ticker.botAuction': { cs: '{name} nemůže {tile} zaplatit — jde do aukce ({why})', en: '{name} cannot fund {tile} — going to auction ({why})' },
  'ticker.botBroke': { cs: '{name} je na mizině a přešel {tile}', en: '{name} is broke and passes on {tile}' },
  'reason.majority51': { cs: 'kupuje 51% kontrolu', en: 'securing 51% control' },
  'reason.buyout100': { cs: 'skupuje celý podnik (100 %)', en: 'buying out the whole tile (100%)' },
  'reason.blockingStake': { cs: 'staví blokovací podíl 26–35 %', en: 'building a 26–35% blocking stake' },
  'reason.consolidate': { cs: 'dokupuje většinu ve známém distriktu', en: 'consolidating a majority in a known district' },
  'reason.tranche': { cs: 'bezpečná tranše se zachováním rezervy', en: 'safe tranche keeping the cash reserve' },
  'reason.districtSet': { cs: 'rozšiřuje distriktní set', en: 'expanding the district set' },
  'reason.blockMonopoly': { cs: 'blokuje soupeřův monopol', en: 'blocking a rival monopoly' },
  'reason.completeSet': { cs: 'kompletuje distriktní set', en: 'completing the district set' },
  'reason.distressed': { cs: 'vykupuje zlevněné akcie v krizi', en: 'bottom-fishing distressed shares' },
  'reason.maxAffordable': { cs: 'bere maximum, na co má', en: 'taking the biggest bite it can fund' },
  'reason.premium': { cs: 'prémiový distrikt', en: 'premium district bite' },
  'reason.auction': { cs: 'zkouší štěstí v aukci', en: 'gambling at auction' },
  'reason.reserve': { cs: 'chrání rezervu', en: 'protecting the reserve' },


  // ---- tile names (CZ/EN) ----
  'tile.Payroll Plaza': { cs: 'Výplatní náměstí', en: 'Payroll Plaza' },
  'tile.Townhouse': { cs: 'Řadový dům', en: 'Townhouse' },
  'tile.Corner Shop': { cs: 'Krámek na rohu', en: 'Corner Shop' },
  'tile.Station North': { cs: 'Stanice Sever', en: 'Station North' },
  'tile.Bakery': { cs: 'Pekárna', en: 'Bakery' },
  'tile.Cafe': { cs: 'Kavárna', en: 'Cafe' },
  'tile.Bookstore': { cs: 'Knihkupectví', en: 'Bookstore' },
  'tile.Audit & Tax Bureau': { cs: 'Úřad auditu a daní', en: 'Audit & Tax Bureau' },
  'tile.Bank Building': { cs: 'Bankovní dům', en: 'Bank Building' },
  'tile.Investment Firm': { cs: 'Investiční firma', en: 'Investment Firm' },
  'tile.Station East': { cs: 'Stanice Východ', en: 'Station East' },
  'tile.Stock Exchange': { cs: 'Akciová burza', en: 'Stock Exchange' },
  'tile.Insurance HQ': { cs: 'Pojišťovna', en: 'Insurance HQ' },
  'tile.Startup Incubator': { cs: 'Startovací inkubátor', en: 'Startup Incubator' },
  'tile.Investment Bank': { cs: 'Investiční banka', en: 'Investment Bank' },
  'tile.Tech Hub': { cs: 'Technologické centrum', en: 'Tech Hub' },
  'tile.Station South': { cs: 'Stanice Jih', en: 'Station South' },
  'tile.AI Lab': { cs: 'Laboratoř AI', en: 'AI Lab' },
  'tile.Bio Center': { cs: 'Biocentrum', en: 'Bio Center' },
  'tile.Metropolis Tower': { cs: 'Metropolitní věž', en: 'Metropolis Tower' },
  'tile.Skyscraper': { cs: 'Mrakodrap', en: 'Skyscraper' },
  'tile.Securities Exchange': { cs: 'Burza cenných papírů', en: 'Securities Exchange' },
  'tile.Station West': { cs: 'Stanice Západ', en: 'Station West' },
  'tile.Metropolis Mall': { cs: 'Metropolitní pasáž', en: 'Metropolis Mall' },
  'tile.Capital Center': { cs: 'Kapitálové centrum', en: 'Capital Center' },
  'tile.Luxury Tower': { cs: 'Luxusní věž', en: 'Luxury Tower' },
  'tile.Suburb Terraces': { cs: 'Předměstské terasy', en: 'Suburb Terraces' },
  'tile.Growth Quarter': { cs: 'Čtvrť růstu', en: 'Growth Quarter' },
  'tile.Industrial Park': { cs: 'Průmyslový park', en: 'Industrial Park' },
  'tile.Tech Hub Annex': { cs: 'Přístavek Tech Hubu', en: 'Tech Hub Annex' },

  // ---- special tile guide (interactive explainer) ----
  'guide.title': { cs: 'Jak toto pole funguje', en: 'How this tile works' },
  'guide.tactic': { cs: 'Taktika', en: 'Tactical edge' },
  'guide.tapHint': { cs: 'Klepnutím na speciální pole otevřeš tohoto průvodce.', en: 'Tap a special tile to open this guide.' },
  'guide.transit.title': { cs: '🚂 Tranzitní stanice & Metro', en: '🚂 Transit Hub & Metro' },
  'guide.transit.b1': { cs: 'Příjezd: dividenda $60 — rozdělí se akcionářům podle podílu, zbytek bere návštěvník.', en: 'Landing: $60 dividend — split pro-rata to shareholders, the visitor keeps the remainder.' },
  'guide.transit.b2': { cs: 'Rychlocesta metrem: za $200 se přesuneš na kteroukoli jinou stanici (1× za tah).', en: 'Metro fast-travel: pay $200 to jump to any other station (once per turn).' },
  'guide.transit.b3': { cs: 'Tranzit nelze koupit celý — kupují se jen akcie a dividenda teče každému držiteli.', en: 'Transit cannot be bought outright — you buy shares and every holder collects the dividend.' },
  'guide.transit.tactic': { cs: 'Drž akcie ve více stanicích: každý průjezd soupeře ti platí, aniž bys musel stavět.', en: 'Hold shares in several stations: every rival visit pays you without any building cost.' },
  'guide.cityHall.title': { cs: '🏛️ Úřad auditu a daní (City Hall)', en: '🏛️ Audit & Tax Bureau (City Hall)' },
  'guide.cityHall.b1': { cs: 'Lobbing — stavební uzávěra čtvrti: $1 500. Jedno kolo bez nájmu i staveb.', en: 'Lobbying — zoning freeze on a district: $1,500. One round with no rent and no building.' },
  'guide.cityHall.b2': { cs: 'Daňový audit lídra: $1 000 za akci + 10 % hotovosti nejbohatšího hráče.', en: 'Audit on the cash leader: $1,000 fee + 10% of the richest player’s cash.' },
  'guide.cityHall.b3': { cs: 'Zastavení na tomto poli není paušální daň — vybíráš si z lobby menu.', en: 'Landing here is not a flat tax — you pick an action from the lobby menu.' },
  'guide.cityHall.tactic': { cs: 'Uzávěrku hoď na čtvrť, kde soupeř drží monopol — kolo bez nájmu smaže jeho zisk.', en: 'Freeze the district where a rival holds a monopoly — one rent-free round erases their profit.' },
  'guide.financial.title': { cs: '🏦 Investiční banka / Burza — Finanční stůl', en: '🏦 Investment Bank / Exchange — Financial Desk' },
  'guide.financial.b1': { cs: 'Strukturované úvěry na 3 / 5 / 8 kol: anuitní, úrokové nebo balónové. Sazba podle cyklu — BOOM 4 %, STAGNACE 7 %, KRIZE 12 %.', en: 'Structured loans over 3 / 5 / 8 rounds: annuity, interest-only or balloon. Cycle-linked rate — BOOM 4%, STAGNATION 7%, CRISIS 12%.' },
  'guide.financial.b2': { cs: 'Tři fondy pro volnou hotovost: konzervativní 3 %, vyvážený REIT ~8 % (horizont 3 kola), dynamický ~18 % (v krizi −15 %).', en: 'Three treasury funds for idle cash: conservative 3%, balanced REIT ~8% (3-round horizon), venture ~18% (−15% in a crisis).' },
  'guide.financial.b3': { cs: 'Pojištění ve třech úrovních: základní 2 % (3 kola, 50 % nájmu), komplexní 5 % (4 kola, 75 %) a VIP štít 8 % (6 kol, 90 % + imunita proti převzetí). Předčasné splacení úvěru stojí 2,5 %.', en: 'Three insurance tiers: Basic 2% (3 rounds, 50% of rent), Standard 5% (4 rounds, 75%) and the VIP Executive Shield 8% (6 rounds, 90% + takeover immunity). Early loan payoff costs a flat 2.5%.' },
  'guide.financial.b4': { cs: 'Úvěrový limit = 50 % likvidního čistého jmění a hlídá i strukturované úvěry.', en: 'Credit ceiling = 50% of liquid net worth and nets out structured loans too.' },
  'guide.financial.tactic': { cs: 'V boomu je kredit levný — páku ber na IPO aukce, v krizi drž hotovost ve fondech.', en: 'Credit is cheap in a boom — lever up for IPO auctions; in a crisis park cash in the funds.' },
  'guide.payroll.title': { cs: '💰 Výplatní náměstí (Start)', en: '💰 Payroll Plaza (Start)' },
  'guide.payroll.b1': { cs: 'Průchod i zastavení na startu vynáší $2 000 (v boomu ×1,25, v krizi ×0,75).', en: 'Passing or landing on Start pays $2,000 (×1.25 in a boom, ×0.75 in a crisis).' },
  'guide.payroll.b2': { cs: 'Push-your-luck: pokud přes start projdeš riskantním hodem, výplata se násobí ×2.', en: 'Push-your-luck: passing Start with the risky extra roll doubles the payout (×2).' },
  'guide.payroll.b3': { cs: 'Nevyužité body pohybu stojí pokutu $100 za krok.', en: 'Unused movement points cost an idle penalty of $100 per step.' },
  'guide.payroll.tactic': { cs: 'Plánuj zastávky tak, aby smyčka končila na startu — hotovost na aukce je vždy výhoda.', en: 'Plan stops so your loop closes on Start — auction cash is always an edge.' },
  'guide.commercial.title': { cs: '🏙️ Komerční pozemek', en: '🏙️ Commercial plot' },
  'guide.commercial.b1': { cs: 'Kontrola = více než 50 % akcií. Vlastni všechny pozemky čtvrti (3–5) a získáš monopol +25 % nájmu.', en: 'Control = more than 50% of shares. Own every plot in the colour group (3–5) for a monopoly: +25% rent.' },
  'guide.commercial.b2': { cs: 'Nájem roste s valuací, živým výnosem 10–25 % a s každým tierem (tier 5 = Megastruktura).', en: 'Rent scales with valuation, the live 10–25% yield and each tier (tier 5 = Megastructure).' },
  'guide.commercial.tactic': { cs: '51 % už stačí na kontrolu pozemku — zbytek může zůstat ve veřejném floatu.', en: 'A 51% stake already controls a tile — the rest can stay in the public float.' },

  'tile.Skyline Annex': { cs: 'Přístavek skyline', en: 'Skyline Annex' },


  // ---- Financial Desk ----
  'desk.subtitle': { cs: 'Korporátní governance', en: 'Corporate governance' },
  'desk.title': { cs: '🏦 Finanční deska', en: '🏦 Financial Desk' },
  'desk.open': { cs: '🏦 Otevřít finanční desku · úvěry & investice', en: '🏦 Open Financial Desk · loans & investments' },
  'desk.tabLoans': { cs: '🏦 Úvěry · Půjčky ({count})', en: '🏦 Loans · Půjčky ({count})' },
  'desk.tabInvestments': { cs: '📈 Investice · Spoření ({value})', en: '📈 Investments · Savings ({value})' },
  'desk.baseRate': { cs: 'Základní sazba {rate}/kolo', en: 'Base rate {rate}/round' },
  'desk.creditLine': { cs: 'Úvěrový limit {value}', en: 'Credit line {value}' },
  'desk.used': { cs: 'Čerpáno {value}', en: 'Used {value}' },
  'desk.free': { cs: 'Volné {value}', en: 'Free {value}' },
  'desk.cash': { cs: 'Hotovost {value}', en: 'Cash {value}' },
  'desk.loanStructuring': { cs: 'Struktura úvěru', en: 'Loan structuring' },
  'desk.principal': { cs: 'Jistina', en: 'Principal' },
  'desk.term': { cs: 'Splatnost', en: 'Term' },
  'desk.termRounds': { cs: '{rounds} kol', en: '{rounds} rounds' },
  'desk.rateNote': {
    cs: 'Sazba se uzamkne při sjednání: {rate}/kolo{premium}. Základna jde s cyklem — BOOM 4 % · STAGNACE 7 % · KRIZE 12 %.',
    en: 'Rate locks at origination: {rate}/round{premium}. Base follows the cycle — BOOM 4% · STAGNATION 7% · CRISIS 12%.',
  },
  'desk.premiumSuffix': { cs: ' (základna + 2% riziková prémie)', en: ' (base + 2% risk premium)' },
  'desk.roundPayment': { cs: 'Splátka za kolo', en: 'Round payment' },
  'desk.overpay': { cs: 'Přeplacení', en: 'Total interest' },
  'desk.totalInterest': { cs: 'celkové úroky', en: 'total interest' },
  'desk.maturity': { cs: 'Splatnost', en: 'Maturity' },
  'desk.totalRepayment': { cs: 'Celkem zaplatíš', en: 'Total repayment' },
  'desk.balloonDue': { cs: 'Balónová splátka', en: 'Balloon due' },
  'desk.atMaturity': { cs: 'na konci', en: 'at maturity' },
  'desk.principalPlusInterest': { cs: 'jistina + úroky', en: 'principal + interest' },
  'desk.draw': { cs: '💰 Čerpat {amount} · {structure}', en: '💰 Draw {amount} · {structure}' },
  'desk.exceedsLine': { cs: '⚠ Překračuje limit (volné {free})', en: '⚠ Exceeds credit line (free {free})' },
  'desk.activeLoans': { cs: 'Aktivní úvěry · {outstanding} nesplaceno', en: 'Active loans · {outstanding} outstanding' },
  'desk.perRound': { cs: '{amount}/kolo', en: '{amount}/round' },
  'desk.zeroCoupon': { cs: '$0/kolo (zero-coupon)', en: '$0/round (zero-coupon)' },
  'desk.outstanding': { cs: 'nesplaceno {value}', en: 'outstanding {value}' },
  'desk.roundsLeft': { cs: 'zbývá {rounds} kol', en: '{rounds} round(s) left' },
  'desk.matures': { cs: 'splatnost R{round}', en: 'matures R{round}' },
  'desk.prepayToday': { cs: 'Dnes splatit: {total} (včetně {fee} poplatku {pct})', en: 'Prepay today: {total} (incl. {fee} fee at {pct})' },
  'desk.prepayEarly': { cs: '⏱ Předčasně splatit', en: '⏱ Prepay early' },
  'desk.amount': { cs: 'Částka', en: 'Amount' },
  'desk.invest': { cs: 'Investovat', en: 'Invest' },
  'desk.withdraw': { cs: 'Vybrat', en: 'Withdraw' },
  'desk.expected': { cs: 'Očekávaný výnos', en: 'Expected' },
  'desk.volatility': { cs: 'Volatilita', en: 'Volatility' },
  'desk.liquidity': { cs: 'Likvidita', en: 'Liquidity' },
  'desk.lastRound': { cs: '{pct} za poslední kolo', en: '{pct} last round' },
  'desk.horizonNote': {
    cs: '⏳ Horizont {horizon} · poplatek za předčasný výstup {pct} (drženo {held} kol)',
    en: '⏳ Horizon {horizon} · early exit penalty {pct} (held {held} round(s))',
  },
  'desk.horizonInstant': { cs: 'okamžitě', en: 'instant' },
  'desk.treasury': { cs: 'Pokladna: {live} živě · {invested} vloženo', en: 'Treasury: {live} live · {invested} invested' },
  'desk.fundsNote': {
    cs: 'Fondy se přeceňují jednou za kolo (makro tick) — volná hotovost tak neztrácí inflací.',
    en: 'Funds mark to market once per round (macro tick) — idle cash stops losing to inflation.',
  },

  // ---- Financial Desk · Insurance tab ----
  'desk.tabInsurance': { cs: '🛡️ Pojištění nájmu', en: '🛡️ Rent Insurance' },
  'desk.insuranceNone': {
    cs: 'Žádné aktivní pojištění. Vyberte si tarif níže pro ochranu před vysokými nájmy.',
    en: 'No active insurance policy. Select a plan below to protect against costly rents.',
  },
  'desk.insuranceActive': {
    cs: 'Aktivní pojistka (zbývá {turns} kol, kryje {pct} % nájmu)',
    en: 'Active policy ({turns} turns remaining, covers {pct}% of rent)',
  },
  'desk.insuranceTurnsLeft': { cs: 'kol zbývá', en: 'turn(s) left' },
  'desk.insurancePremium': { cs: 'Pojistné: {price} ({pct} % čistého jmění)', en: 'Premium: {price} ({pct}% of net worth)' },
  'desk.insuranceTerm': { cs: 'Platnost: {turns} kol', en: 'Duration: {turns} turns' },
  'desk.insuranceCoverage': { cs: 'Krytí nájmu: {pct} %', en: 'Rent coverage: {pct}%' },
  'desk.insuranceImmunity': { cs: 'Imunita proti převzetí', en: 'Takeover immunity' },
  'desk.insuranceActivate': { cs: 'Aktivovat pojištění', en: 'Activate insurance' },
  'desk.insuranceActiveCta': { cs: '✓ Aktivní', en: '✓ Active' },
  'desk.insuranceNote': {
    cs: 'Pojistné se počítá jako % z celkového čistého jmění (Net Worth) a strhává se ihned.',
    en: 'Premium is calculated as a % of your Net Worth and charged immediately upon activation.',
  },
  'desk.policy.BASIC': { cs: 'Základní krytí', en: 'Basic Coverage' },
  'desk.policy.BASIC.note': {
    cs: 'Levné krytí pro bezpečnější průchod méně rozvinutými čtvrtěmi.',
    en: 'Affordable protection for safe transit across lower-tier properties.',
  },
  'desk.policy.STANDARD': { cs: 'Komplexní ochrana', en: 'Standard Umbrella' },
  'desk.policy.STANDARD.note': {
    cs: 'Vyvážený poměr ceny a bezpečí pro běžnou hru.',
    en: 'Well-balanced cost and safety for regular gameplay.',
  },
  'desk.policy.VIP': { cs: 'VIP Exekutivní štít', en: 'VIP Executive Shield' },
  'desk.policy.VIP.note': {
    cs: 'Maximální krytí nájmů + 100% imunita proti nepřátelskému převzetí (Hostile Takeover)!',
    en: 'Maximum rent relief + 100% immunity to Hostile Takeovers while active!',
  },

  // ---- player financial dossier ----
  'dossier.title': { cs: '📊 Finanční dossier hráče', en: '📊 Player Financial Dossier' },
  'dossier.bankrupt': { cs: 'BANKROT', en: 'BANKRUPT' },
  'dossier.tabBalance': { cs: '⚖️ Rozvaha & bonita', en: '⚖️ Balance sheet' },
  'dossier.tabCashflow': { cs: '💵 Cash flow', en: '💵 Cash flow' },
  'dossier.tabAudit': { cs: '📜 Audit log', en: '📜 Audit log' },
  'dossier.assets': { cs: 'Aktiva', en: 'Assets' },
  'dossier.liabilities': { cs: 'Pasiva', en: 'Liabilities' },
  'dossier.cash': { cs: 'Hotovost', en: 'Liquid cash' },
  'dossier.equity': { cs: 'Hodnota akcií', en: 'Equity value' },
  'dossier.funds': { cs: 'Vklady ve fondech', en: 'Fund holdings' },
  'dossier.costBasis': { cs: 'vloženo', en: 'cost basis' },
  'dossier.totalAssets': { cs: 'Celková aktiva', en: 'Total assets' },
  'dossier.informalDebt': { cs: 'Nevypořádaný dluh', en: 'Informal debt' },
  'dossier.totalLiabilities': { cs: 'Celková pasiva', en: 'Total liabilities' },
  'dossier.ceiling': { cs: 'Úvěrový strop', en: 'Credit ceiling' },
  'dossier.used': { cs: 'Čerpáno', en: 'Used' },
  'dossier.free': { cs: 'Volné', en: 'Free' },
  'dossier.creditRating': { cs: 'Rating bonity', en: 'Credit rating' },
  'dossier.markupNote': { cs: '⚠️ Riziková přirážka +{pct} na všechny nové úvěry', en: '⚠️ Risk markup +{pct} on all new borrowings' },
  'dossier.netWorth': { cs: 'Čisté jmění', en: 'Net worth' },
  'dossier.activeLoans': { cs: 'Aktivní úvěry', en: 'Active loans' },
  'dossier.holdings': { cs: 'Držené pozice', en: 'Holdings' },
  'dossier.inflows': { cs: 'Příjmy', en: 'Total inflows' },
  'dossier.outflows': { cs: 'Výdaje', en: 'Total outflows' },
  'dossier.totalInflows': { cs: 'Celkem příjmy', en: 'Total inflows' },
  'dossier.totalOutflows': { cs: 'Celkem výdaje', en: 'Total outflows' },
  'dossier.netFlow': { cs: 'Čistý cash-flow', en: 'Net cash flow' },
  'dossier.cashflowNote': { cs: 'Souhrn všech historických toků od začátku partie.', en: 'Aggregated lifetime flows since the match started.' },
  'dossier.rentCollected': { cs: 'Inkasované nájmy', en: 'Rent collected' },
  'dossier.shareSaleProceeds': { cs: 'Výnosy z prodeje akcií', en: 'Share sale proceeds' },
  'dossier.investmentYield': { cs: 'Výnosy ze fondů', en: 'Fund yields' },
  'dossier.transitDividends': { cs: 'Dividendy z dopravy', en: 'Transit dividends' },
  'dossier.loanDraws': { cs: 'Čerpané úvěry', en: 'Loan draws' },
  'dossier.rentPaid': { cs: 'Zaplacené nájmy', en: 'Rent paid' },
  'dossier.sharePurchases': { cs: 'Nákup akcií', en: 'Share purchases' },
  'dossier.buildingUpgrades': { cs: 'Výstavba a vylepšení', en: 'Building upgrades' },
  'dossier.interestPaid': { cs: 'Zaplacené úroky', en: 'Interest paid' },
  'dossier.insurancePremiums': { cs: 'Zaplacené pojistné', en: 'Insurance premiums' },
  'dossier.bankFees': { cs: 'Sankce, daně a poplatky', en: 'Bank fees, penalties & taxes' },
  'dossier.foreclosureSeized': { cs: 'Exekuce bankou', en: 'Bank foreclosure seizure' },
  'dossier.auditEmpty': { cs: 'Zatím žádné transakce.', en: 'No transactions recorded yet.' },

  // ---- portfolio ----
  'portfolio.title': { cs: 'Portfolio po distriktech · District sets', en: 'Portfolio by district · District sets' },
  'portfolio.districtsCount': { cs: '{count} distriktů', en: '{count} districts' },
  'portfolio.empty': { cs: 'Zatím žádné pozice — vyhraj aukci nebo kapitálový call.', en: 'No positions yet — win an auction or a capital call.' },
  'portfolio.district': { cs: '{name} distrikt', en: '{name} District' },
  'portfolio.rentLine': { cs: 'Nájem: {rent} (výnos {yield})', en: 'Rent: {rent} (yield {yield})' },
  'portfolio.districtSize': {
    cs: '{total} pozemků v této čtvrťové skupině (balancováno 3–5)',
    en: '{total} properties in this colour group (balanced 3–5)',
  },
};

